import icon from "@/assets/icon.svg";
import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { OptionBar } from "@/ds/custom/OptionBar";
import { LoadingState } from "@/ds/custom/LoadingState";
import { StatePanel } from "@/ds/custom/StatePanel";
import { Statistic, StatisticsBar } from "@/ds/custom/StatisticsBar";
import { Button } from "@/ds/shadcn/components/ui/button";
import { SidebarProvider } from "@/ds/shadcn/components/ui/sidebar";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import type { PublicTransactionSummaryModel } from "@/features/collections/api/models/PublicTransactionSummaryModel";
import { usePublicCollection } from "@/features/collections/api/usePublicCollection";
import { usePublicCollectionCurrencyAndPriceSource } from "@/features/collections/api/usePublicCollectionCurrencyAndPriceSource";
import { usePublicTransactionSummariesByShareToken } from "@/features/collections/api/usePublicTransactionSummariesByShareToken";
import { PublicTransactionSummariesSortOrderOptions } from "@/features/collections/PublicTransactionSummariesSortOrder";
import { DataView } from "@/features/common/components/dataview/DataView";
import { GalleryItem, GalleryItemProperty } from "@/features/common/components/dataview/GalleryItem";
import { ListItem } from "@/features/common/components/dataview/ListItem";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { HeaderBarProvider } from "@/features/common/components/HeaderBarProvider";
import { HeaderBarSelector } from "@/features/common/components/HeaderBarSelector";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { SearchBar } from "@/features/common/components/SearchBar";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { ViewTypeOptions } from "@/lib/common/ViewType";
import { useTitle } from "@/lib/common/useTitle";
import { CurrencyOptions, type Currency } from "@/lib/domain/Currency";
import { PriceSourceOptions, type PriceSource } from "@/lib/domain/PriceSource";
import { useAuthState } from "@/lib/state";
import { useUserPreferencesState } from "@/lib/state/UserPreferencesState";
import { CopyPlusIcon, PackageOpenIcon, SortAscIcon } from "lucide-react";
import { useMemo, useState, type FC } from "react";
import { Link, useParams } from "react-router";

export const PagePublicCollection: FC = () => {
	const params = useParams();
	const shareToken = params.shareToken as string;

	const { state: userPreferencesState } = useUserPreferencesState();
	const needsCurrencyAndPriceSource = !userPreferencesState.publicCollectionCurrency || !userPreferencesState.publicCollectionPriceSource;
	const publicCollectionCurrencyAndPriceSource = usePublicCollectionCurrencyAndPriceSource(shareToken, needsCurrencyAndPriceSource);

	const currency = userPreferencesState.publicCollectionCurrency || publicCollectionCurrencyAndPriceSource.data?.currency;
	const priceSource = userPreferencesState.publicCollectionPriceSource || publicCollectionCurrencyAndPriceSource.data?.priceSource;

	if (publicCollectionCurrencyAndPriceSource.isError) {
		return (
			<div className="p-3 @md:mt-12 @md:max-w-128 mx-auto">
				<ErrorDisplay error={publicCollectionCurrencyAndPriceSource.error} />
			</div>
		);
	}

	if (!currency || !priceSource) {
		return <LoadingState />;
	}

	return (
		<PagePublicCollectionInternal shareToken={shareToken} currency={currency} priceSource={priceSource} />
	);
};

interface PagePublicCollectionInternalProps {
	shareToken: string;
	currency: Currency;
	priceSource: PriceSource;
}

const PagePublicCollectionInternal: FC<PagePublicCollectionInternalProps> = props => {
	const {
		shareToken
	} = props;

	const { state: authState } = useAuthState();

	const {
		state: userPreferencesState,
		dispatchSetViewTypePreference,
		dispatchSetPublicCollectionCurrency,
		dispatchSetPublicCollectionPriceSource,
		dispatchSetPublicTransactionSummariesSortOrder
	} = useUserPreferencesState();

	const [searchQuery, setSearchQuery] = useState("");

	const currency = userPreferencesState.publicCollectionCurrency || props.currency;
	const priceSource = userPreferencesState.publicCollectionPriceSource || props.priceSource;

	const publicCollection = usePublicCollection(shareToken, currency, priceSource);
	const transactionSummaries = usePublicTransactionSummariesByShareToken(shareToken, currency, priceSource, searchQuery, userPreferencesState.publicTransactionSummariesSortOrder);
	useTitle(publicCollection.data ? publicCollection.data.name : "Public Collection");

	const additionalProps = useMemo(() => ({ currency }), [currency]);

	return (
		<SidebarProvider>
			<main className="w-full min-h-svh bg-background">
				<HeaderBarProvider>
					<div className="flex items-center justify-between gap-3 p-4 border-b">
						<Link to="/login" className="flex items-center gap-2 font-medium">
							<img src={icon} className="h-6 w-6" />
							Fyendal&apos;s Collection
						</Link>

						{
							!authState.accessToken &&
							<Link to="/login/register">
								<Button variant="outline">Create Account</Button>
							</Link>
						}
					</div>

					<HeaderBar
						title={publicCollection.data?.name || "Public Collection"}
						description={publicCollection.data ? `Owned by ${publicCollection.data.displayName}` : ""}
						showSideBarTrigger={false} />

					<HeaderBarTools>
						<HeaderBarSelector
							value={priceSource}
							onChange={dispatchSetPublicCollectionPriceSource}
							placeholder="Price Source"
							options={PriceSourceOptions} />

						<HeaderBarSelector
							value={currency}
							onChange={dispatchSetPublicCollectionCurrency}
							placeholder="Currency"
							options={CurrencyOptions} />

						<HeaderBarSelector
							value={userPreferencesState.publicTransactionSummariesSortOrder}
							onChange={dispatchSetPublicTransactionSummariesSortOrder}
							placeholder="Sort"
							icon={SortAscIcon}
							options={PublicTransactionSummariesSortOrderOptions} />

						<OptionBar
							className="w-full @md:w-32"
							options={ViewTypeOptions}
							value={userPreferencesState.viewTypePreference}
							onChange={dispatchSetViewTypePreference} />

						{
							authState.accessToken && !!publicCollection.data && publicCollection.data.totalQuantity > 0 &&
							<Link to={`/public/${shareToken}/clone-public-collection?currency=${currency}&priceSource=${priceSource}`} target="_blank" rel="noopener noreferrer">
								<Button variant="outline" className="w-full @md:w-24">
									<CopyPlusIcon />
									Clone
								</Button>
							</Link>
						}
					</HeaderBarTools>

					{
						publicCollection.isLoading &&
						<LoadingState />
					}

					{
						publicCollection.isError &&
						<div className="p-3">
							<ErrorDisplay error={publicCollection.error} />
						</div>
					}

					{
						publicCollection.data &&
						<StatisticsBar>
							<Statistic title="Total Quantity" className="type-stat">
								{publicCollection.data.totalQuantity}
							</Statistic>
							<Statistic title="Total Market Value">
								<MoneyDisplay className="type-stat" preferredCurrency={currency} money={publicCollection.data.totalMarketValue} mode="Standard" />
							</Statistic>
						</StatisticsBar>
					}

					<ErrorDisplay error={transactionSummaries.error} />

					<SearchBar value={searchQuery} onChange={setSearchQuery} />

					<DataView
						forQuery={transactionSummaries}
						getKey={x => x.variantId}
						listViewHeaderLabels={["Product", "Value", "Quantity", "Total"]}
						listViewHeaderClassNames={["min-w-0", "w-24 @2xl:w-32", "w-24", "w-24 @2xl:w-32"]}
						listComponent={PublicTransactionSummaryListItem}
						galleryComponent={PublicTransactionSummaryGalleryItem}
						additionalProps={additionalProps}
						galleryMinWidth={208}>

						<StatePanel
							icon={PackageOpenIcon}
							title="No Cards Found"
							description="This collection doesn't have any cards matching your search yet." />
					</DataView>
				</HeaderBarProvider>
			</main>
		</SidebarProvider>
	);
};

interface PublicTransactionSummaryAdditionalProps {
	currency: Currency;
}

const PublicTransactionSummaryListItem: FC<PublicTransactionSummaryModel & PublicTransactionSummaryAdditionalProps> = props => {
	const {
		productId,
		productName,
		fcId,
		imageUrl,
		quantity,
		marketValue,
		totalMarketValue,
		currency
	} = props;

	return (
		<ListItem className="relative hover:bg-background-highlight dark:even:hover:bg-background-highlight transition-colors duration-150">
			<TableCell>
				<Link to={`/browse/product/${productId}`} className="after:absolute after:inset-0">
					<SmallProductDisplay productName={productName} fcId={fcId} imageUrl={imageUrl} />
				</Link>
			</TableCell>
			<TableCell><MoneyDisplay mode="Standard" preferredCurrency={currency} money={marketValue} /></TableCell>
			<TableCell>{quantity}</TableCell>
			<TableCell><MoneyDisplay mode="Standard" preferredCurrency={currency} money={totalMarketValue} /></TableCell>
		</ListItem>
	);
};

const PublicTransactionSummaryGalleryItem: FC<PublicTransactionSummaryModel & PublicTransactionSummaryAdditionalProps> = props => {
	const {
		productId,
		productName,
		fcId,
		imageUrl,
		quantity,
		marketValue,
		totalMarketValue,
		currency
	} = props;

	return (
		<GalleryItem title={productName} description={fcId} imageUrl={imageUrl} imageLink={`/browse/product/${productId}`}>
			<GalleryItemProperty label="Value">
				<MoneyDisplay mode="Standard" preferredCurrency={currency} money={marketValue} />
			</GalleryItemProperty>
			<GalleryItemProperty label="Quantity">
				{quantity}
			</GalleryItemProperty>
			<GalleryItemProperty label="Total">
				<MoneyDisplay mode="Standard" preferredCurrency={currency} money={totalMarketValue} />
			</GalleryItemProperty>
		</GalleryItem>
	);
};
